import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Upload, FileText, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface ExtractedData {
  deal_title?: string;
  seller_name?: string;
  seller_email?: string;
  seller_phone?: string;
  seller_address?: string;
  buyer_name?: string;
  buyer_address?: string;
  date_of_sale?: string;
  first_payment_date?: string;
  sales_price?: number;
  down_payment_amount?: number;
  beginning_balance?: number;
  interest_rate?: number;
  payment_amount?: number;
  original_term_months?: number;
  has_balloon_payment?: boolean;
  balloon_amount?: number;
  balloon_due_date?: string;
  property_type?: string;
  property_address?: string;
  property_description?: string;
  estimated_property_value?: number;
  summary?: string;
}

interface ContractUploadProps {
  onDataExtracted: (data: ExtractedData) => void;
}

const ContractUpload = ({ onDataExtracted }: ContractUploadProps) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [summary, setSummary] = useState<string | null>(null);
  const { toast } = useToast();

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast({
        title: 'Invalid file type',
        description: 'Please upload a PDF file',
        variant: 'destructive',
      });
      return;
    }

    setIsProcessing(true);
    setSummary(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const { data, error } = await supabase.functions.invoke('extract-contract-data', {
        body: formData,
      });

      if (error) throw error;

      if (data) {
        const summaryText = `✅ Extracted: ${data.deal_title || 'Contract'}\n💰 Current Balance: $${data.current_balance?.toLocaleString() || 'N/A'}\n📊 ${data.payments_made || 0} payments made`;
        setSummary(summaryText);
        onDataExtracted(data);
        toast({
          title: 'Success! 🎉',
          description: 'Contract data extracted and current balance calculated automatically.',
        });
      }
    } catch (error) {
      console.error('Error extracting contract data:', error);
      toast({
        title: 'Extraction Failed',
        description: 'Could not extract data from the PDF. Please check the file and try again.',
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
      e.target.value = '';
    }
  };

  return (
    <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-primary" />
          🤖 Bot #1: Contract Intelligence (Powered by Claude)
        </CardTitle>
        <CardDescription>
          Upload your land contract PDF - AI will extract data AND calculate current balance
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            disabled={isProcessing}
            className="relative"
            onClick={() => document.getElementById('contract-upload')?.click()}
          >
            {isProcessing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                Choose PDF File
              </>
            )}
          </Button>
          <input
            id="contract-upload"
            type="file"
            accept="application/pdf"
            onChange={handleFileUpload}
            className="hidden"
          />
        </div>

        {summary && (
          <Alert>
            <AlertDescription>
              <strong>Summary:</strong> {summary}
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default ContractUpload;
